// CR404: OCR Run Control Buttons
// Provides Force Stop control for S1.2 OCR runs

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { StopCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface OcrRunControlButtonsProps {
  batchRunId: string;
  flyerScanRunId?: string;
  isRunning?: boolean;
}

export function OcrRunControlButtons({
  batchRunId,
  flyerScanRunId,
  isRunning = true,
}: OcrRunControlButtonsProps) {
  const [isStopLoading, setIsStopLoading] = useState(false);

  const resolveTargetRunId = async (): Promise<string | null> => {
    // Prefer explicit run id when it resolves to a real dashboard row.
    if (flyerScanRunId) {
      const { data: direct } = await supabase
        .from("flyer_scan_runs_v2")
        .select("id")
        .eq("id", flyerScanRunId)
        .maybeSingle();
      if (direct?.id) {
        return direct.id;
      }
    }

    // Fallback: latest active run linked to this batch.
    const { data: activeRows } = await supabase
      .from("flyer_scan_runs_v2")
      .select("id")
      .eq("discovery_debug->>cr075_batch_run_id", batchRunId)
      .in("status", ["RUNNING", "IN_PROGRESS", "QUEUED"])
      .order("created_at", { ascending: false })
      .limit(1);
    if (activeRows?.[0]?.id) {
      return activeRows[0].id;
    }

    // Last-ditch fallback: latest run for this batch (may already be terminal).
    const { data: latestRows } = await supabase
      .from("flyer_scan_runs_v2")
      .select("id")
      .eq("discovery_debug->>cr075_batch_run_id", batchRunId)
      .order("created_at", { ascending: false })
      .limit(1);
    return latestRows?.[0]?.id ?? null;
  };

  // Force Stop: Set force_stop_requested = true
  const handleForceStop = async () => {
    setIsStopLoading(true);
    try {
      const nowIso = new Date().toISOString();
      const targetRunId = await resolveTargetRunId();
      if (!targetRunId) {
        toast.error("No OCR run found to force stop");
        return;
      }

      const { error } = await supabase
        .from("flyer_scan_runs_v2")
        .update({
          force_stop_requested: true,
          stop_requested_at: nowIso,
          status: "FAILED",
          stop_reason: "FORCE_STOP",
          failure_reason: "Force stopped by user",
          completed_at: nowIso,
          last_progress_at: nowIso,
        })
        .eq("id", targetRunId);

      if (error) throw error;

      // Also update the step attempt to FAILED status
      await supabase
        .from("cr075_batch_run_step_attempts")
        .update({
          status: "FAILED",
          error_message: "Force stopped by user",
          finished_at: nowIso,
        })
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .in("status", ["RUNNING", "IN_PROGRESS", "QUEUED", "PARTIAL"]);

      // Update the step record too
      await supabase
        .from("cr075_batch_run_steps")
        .update({
          status: "FAILED",
          error_message: "Force stopped by user",
          finished_at: nowIso,
        })
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .in("status", ["RUNNING", "IN_PROGRESS"]);

      toast.success("OCR run force stopped");
    } catch (err) {
      console.error("[OcrRunControl] Force stop error:", err);
      toast.error("Failed to request force stop");
    } finally {
      setIsStopLoading(false);
    }
  };

  if (!isRunning) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {/* Force Stop Button with confirmation */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="destructive"
            disabled={isStopLoading}
            className="h-7 text-xs"
          >
            {isStopLoading ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <StopCircle className="h-3 w-3 mr-1" />
            )}
            Force Stop
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force Stop OCR Scan?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately halt the OCR scan after the current page finishes.
              Any pages not yet processed will remain unscanned. The run will be marked as FAILED.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleForceStop} className="bg-destructive text-destructive-foreground">
              Force Stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
